import { usePaymentRetries } from '@/features/paymentRetries/hooks/usePaymentRetries';
import type { PaymentRetry, PaymentRetryFilters } from '@/features/paymentRetries/types';
import { ConfirmModal } from '@/features/subscriptions';
import type { EntityListColumn } from '@/shared/components/EntityList';
import { EntityList } from '@/shared/components/EntityList';
import { PageLayout } from '@/shared/components/PageLayout';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { USER_PERMISSIONS } from '@/shared/types/permissions';
import { formatDate } from '@/shared/utils/date';
import type { AppDispatch } from '@/store';
import { CAlert, CBadge, CButton } from '@coreui/react';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { cancelPaymentRetry } from '../../store/slice';

const PaymentRetriesList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { hasPermission } = usePermissions();

  // State for confirmation modal and success notification
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPaymentRetry, setSelectedPaymentRetry] = useState<PaymentRetry | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const {
    paymentRetries,
    error,
    page,
    perPage,
    totalPages,
    total,
    filters,
    handlePageChange,
    handlePerPageChange,
    handleFiltersChange,
    handleClearFilters,
  } = usePaymentRetries();

  // Handlers for cancel functionality
  const handleCancelClick = (paymentRetry: PaymentRetry) => {
    setSelectedPaymentRetry(paymentRetry);
    setShowConfirmModal(true);
    setSuccessMessage(null);
  };

  const handleConfirmCancel = async () => {
    if (!selectedPaymentRetry) return;

    setIsCancelling(true);
    try {
      await dispatch(cancelPaymentRetry(selectedPaymentRetry.id)).unwrap();
      setSuccessMessage('Payment retry cancelled successfully');
      setShowConfirmModal(false);
      setSelectedPaymentRetry(null);
    } catch (error) {
      console.error('Failed to cancel payment retry:', error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCancelModal = () => {
    setShowConfirmModal(false);
    setSelectedPaymentRetry(null);
  };

  const isFiltersEmpty = (filters: PaymentRetryFilters) => {
    return !filters.search && !filters.shopifyCustomerId;
  };

  const columns: EntityListColumn<PaymentRetry>[] = [
    {
      header: 'Status',
      key: 'status',
      render: (paymentRetry: PaymentRetry) => {
        const getStatusColor = (status: string) => {
          switch (status) {
            case 'pending':
              return 'warning';
            case 'processing':
              return 'info';
            case 'cancelled':
              return 'secondary';
            default:
              return 'light';
          }
        };

        return <CBadge color={getStatusColor(paymentRetry.status)}>{paymentRetry.status}</CBadge>;
      },
    },
    {
      header: 'Shopify Customer ID',
      key: 'shopifyCustomerId',
    },
    {
      header: 'Created At',
      key: 'createdAt',
      render: (paymentRetry: PaymentRetry) => formatDate(paymentRetry.createdAt),
    },
    {
      header: 'Updated At',
      key: 'updatedAt',
      render: (paymentRetry: PaymentRetry) => formatDate(paymentRetry.updatedAt),
    },
    {
      header: 'Actions',
      key: 'actions',
      render: (paymentRetry: PaymentRetry) => {
        if (
          !['pending', 'processing'].includes(paymentRetry.status) ||
          !hasPermission(USER_PERMISSIONS.CustomersPaymentRetriesCancel)
        ) {
          return null;
        }
        return (
          <CButton
            color="danger"
            size="sm"
            onClick={() => handleCancelClick(paymentRetry)}
            disabled={isCancelling}
          >
            Cancel
          </CButton>
        );
      },
    },
  ];

  return (
    <PageLayout>
      {successMessage && (
        <CAlert
          color="success"
          dismissible
          onClose={() => setSuccessMessage(null)}
          className="mb-3"
        >
          {successMessage}
        </CAlert>
      )}

      <EntityList
        title="Payment Retries"
        items={paymentRetries}
        error={error}
        currentPage={page}
        perPage={perPage}
        totalPages={totalPages}
        total={total}
        columns={columns}
        filters={filters}
        filterConfigs={[{ type: 'search', key: 'search', placeholder: 'Shopify Customer ID...' }]}
        createEndpoint=""
        updateEndpoint={() => ''}
        onDelete={() => {}}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
        isFiltersEmpty={isFiltersEmpty}
        requiredViewPermission={USER_PERMISSIONS.CustomersPaymentRetriesView}
        requiredManagePermission={USER_PERMISSIONS.CustomersPaymentRetriesCancel}
      />

      <ConfirmModal
        visible={showConfirmModal}
        title="Cancel Payment Retry"
        message="Are you sure you want to cancel this payment retry? This action cannot be undone."
        confirmText="Cancel Payment Retry"
        confirmColor="danger"
        loading={isCancelling}
        onConfirm={(...args: Parameters<typeof handleConfirmCancel>) =>
          void handleConfirmCancel(...args)
        }
        onCancel={handleCancelModal}
      />
    </PageLayout>
  );
};

export { PaymentRetriesList };
